import React from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * SponsoredRowBlock — fuldbredde banner-reklame mellem opskrifter.
 *
 * ÉT bannerbillede pr. række, ingen produktkarrusel. Bannerets HØJDE
 * følger nu billedets naturlige proportioner — der er INGEN min-height
 * eller fast højde der beskærer billedet. 100% bredde af opskrifts-
 * griddet via `col-span-full`.
 *
 * `height_size` (small/medium/large) styrer nu KUN den vertikale
 * afstand OMKRING banneret + tekst-paddingen, ikke selve billed-
 * containeren.
 *
 * Layout:
 *   - Billede: `<img class="block w-full h-auto">` — naturlig ratio,
 *     aldrig beskåret. Fylder 100% i bredden.
 *   - Semi-transparent overlay + tekst-indhold ligger absolut over
 *     billedet, så billedet dikterer højden af hele banneret.
 *
 * Klik-tracking:
 *   • Rigtige aktive rækker → POST /api/sponsored-rows/{id}/click, åbn i ny fane
 *   • Placeholder / promo-rækker (is_promo=true) → gå til /advertise
 */

// Vertikal afstand omkring banneret. Påvirker IKKE billedets størrelse.
const SPACING_CLASSES = {
  small:  'my-3 sm:my-4',
  medium: 'my-6',
  large:  'my-8 sm:my-10',
};

// Padding INDE i tekst-overlayen. Påvirker heller ikke billedets størrelse
// — den justerer kun hvor luftigt tekst/CTA sidder.
const OVERLAY_PADDING = {
  small:  'p-4 sm:p-5 gap-2',
  medium: 'p-6 sm:p-8 gap-3',
  large:  'p-8 sm:p-10 gap-4',
};

const CTA_BUTTON_CLASSES = {
  blue:    'bg-blue-700 hover:bg-blue-800 text-white',
  green:   'bg-emerald-700 hover:bg-emerald-800 text-white',
  amber:   'bg-amber-600 hover:bg-amber-700 text-white',
  neutral: 'bg-gray-900 hover:bg-black text-white',
};

const TINT_OVERLAY = {
  blue:    'from-blue-900/70 via-blue-900/40 to-transparent',
  green:   'from-emerald-900/70 via-emerald-900/40 to-transparent',
  amber:   'from-amber-900/70 via-amber-900/40 to-transparent',
  neutral: 'from-gray-900/70 via-gray-900/40 to-transparent',
};

const SponsoredRowBlock = ({ row }) => {
  if (!row || !row.banner_image) return null;

  const isPromo = row.is_promo === true;
  const goesToAdvertise = isPromo || !row.cta_link;
  const ctaHref = goesToAdvertise ? '/advertise' : row.cta_link;
  const linkProps = goesToAdvertise
    ? { href: ctaHref }
    : { href: ctaHref, target: '_blank', rel: 'noopener noreferrer sponsored' };

  const trackClick = async () => {
    if (goesToAdvertise || !row.id) return;
    try {
      await axios.post(`${API}/sponsored-rows/${row.id}/click`);
    } catch (err) {
      console.error('[SponsoredRowBlock] click tracking failed:', err);
    }
  };

  const spacingCls = SPACING_CLASSES[row.height_size] || SPACING_CLASSES.medium;
  const overlayPadCls = OVERLAY_PADDING[row.height_size] || OVERLAY_PADDING.medium;
  const btnCls = CTA_BUTTON_CLASSES[row.tint] || CTA_BUTTON_CLASSES.blue;
  const overlayCls = TINT_OVERLAY[row.tint] || TINT_OVERLAY.blue;

  return (
    <a
      {...linkProps}
      onClick={trackClick}
      data-testid={`sponsored-row-${row.id || 'placeholder'}`}
      data-ad-placement="sponsored_row"
      className={`col-span-full ${spacingCls} relative block overflow-hidden rounded-2xl shadow-sm hover:shadow-md transition-shadow no-underline group`}
    >
      {/* Bannerbillede — naturlig aspect ratio, aldrig beskåret.
          Dikterer højden af hele banneret. */}
      <img
        src={row.banner_image}
        alt={row.heading || row.sponsor_name}
        className="block w-full h-auto"
        loading="lazy"
        onError={(e) => { e.target.style.display = 'none'; }}
      />

      {/* Gradient-overlay for tekstlæsbarhed (venstre halvdel mørkere) */}
      <div className={`absolute inset-0 bg-gradient-to-r ${overlayCls} pointer-events-none`} />

      {/* Sponsoreret-label øverst til højre */}
      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-[10px] font-semibold text-gray-700 uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
        Sponsoreret · {row.sponsor_name}
      </div>

      {/* Indhold — venstre-alignet over billedet */}
      <div className={`absolute inset-0 flex flex-col justify-center max-w-2xl ${overlayPadCls}`}>
        {row.sponsor_logo && (
          <img
            src={row.sponsor_logo}
            alt={row.sponsor_name}
            className="max-h-8 max-w-[140px] object-contain bg-white/90 rounded px-2 py-1 self-start"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight drop-shadow">
          {row.heading}
        </h3>
        {row.subheading && (
          <p className="text-sm sm:text-base text-white/90 leading-relaxed drop-shadow max-w-lg">
            {row.subheading}
          </p>
        )}
        <div>
          <span
            className={`inline-block px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow ${btnCls}`}
            data-testid={`sponsored-row-cta-${row.id || 'placeholder'}`}
          >
            {row.cta_text || 'Læs mere'}
          </span>
        </div>
      </div>
    </a>
  );
};

export default SponsoredRowBlock;
